import { defineStore } from 'pinia';
import { api } from '../api/client';

interface State {
  ids: Set<string>;
  loading: boolean;
  loaded: boolean;
}

interface FavoriteRow {
  productId: string;
}

export const useFavoritesStore = defineStore('favorites', {
  state: (): State => ({
    ids: new Set<string>(),
    loading: false,
    loaded: false,
  }),
  getters: {
    isFavorite: (s) => (productId: string) => s.ids.has(productId),
    count: (s) => s.ids.size,
  },
  actions: {
    async fetch() {
      if (this.loaded || this.loading) return;
      this.loading = true;
      try {
        const { data } = await api.get<{ items: FavoriteRow[] }>('/favorites', {
          params: { limit: 200 },
        });
        this.ids = new Set(data.items.map((i) => i.productId));
        this.loaded = true;
      } catch {
        // Not authenticated or endpoint unavailable — leave store empty.
      } finally {
        this.loading = false;
      }
    },
    async toggle(productId: string): Promise<boolean> {
      const wasOn = this.ids.has(productId);
      if (wasOn) this.ids.delete(productId);
      else this.ids.add(productId);
      try {
        if (wasOn) {
          await api.delete(`/favorites/${productId}`);
        } else {
          await api.post('/favorites', { productId });
        }
        return !wasOn;
      } catch (err) {
        // Roll back optimistic update on failure.
        if (wasOn) this.ids.add(productId);
        else this.ids.delete(productId);
        throw err;
      }
    },
    reset() {
      this.ids = new Set<string>();
      this.loaded = false;
    },
  },
});
